import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoginInformationService } from './login-information.service';
import { EntitlementsService } from '../services/entitlements.service';
import { TenantOnboardingService } from '../services/tenant-onboarding.service';
import { isFakeLoginEnabled, isAllowedRedirectUrl } from '../shared/environment.util';

const REQUIRED_ENTITLEMENT_IDS = ['KBJCONFIG', 'KBJPROCES'];
const UNAUTHORIZED_URL = '/unauthorized';
const REVALIDATE_AFTER_MS = 5 * 60 * 1000;

export const requireYfSsidGuard: CanActivateChildFn = () => {
  const document = inject(DOCUMENT);
  const router = inject(Router);
  const loginInformationService = inject(LoginInformationService);
  const entitlementsService = inject(EntitlementsService);
  const tenantOnboardingService = inject(TenantOnboardingService);

  const goToUnauthorized = () => {
    document.location.assign(UNAUTHORIZED_URL);
    return false;
  };

  // 401 (unauthenticated) -> external login; 403 (forbidden) -> /unauthorized.
  const goToLogin = () => {
    if (isAllowedRedirectUrl(environment.loginRedirectUrl)) {
      document.location.assign(environment.loginRedirectUrl);
      return false;
    }
    return router.createUrlTree(['/Login']);
  };

  if (entitlementsService.initialized()) {
    if (!entitlementsService.needsRevalidation(REVALIDATE_AFTER_MS)) {
      return of(true);
    }
    // Session is cached but stale: confirm the cookie is still valid server-side.
    return loginInformationService.validate().pipe(
      map((result) => {
        if (result === 'valid') {
          entitlementsService.markValidated();
          return true;
        }
        entitlementsService.clear();
        tenantOnboardingService.clear();
        loginInformationService.clearCache();
        return result === 'forbidden' ? goToUnauthorized() : goToLogin();
      })
    );
  }

  return loginInformationService.validate().pipe(
    switchMap((result) => {
      if (result === 'forbidden') {
        return of(goToUnauthorized());
      }
      if (result !== 'valid') {
        return of(goToLogin());
      }

      return loginInformationService.getUserInfo().pipe(
        switchMap((userInfo) => {
          if (!userInfo || userInfo.is_privileged !== true) {
            return of(goToUnauthorized());
          }

          // Here we set the entitlements in the EntitlementsService, 
          // so that they can be used throughout the app.
          return loginInformationService.getEntitlements().pipe(
            map((response) => {
              const entitlements = response?.entitlements;
              if (!entitlements || entitlements.length === 0) {
                return goToUnauthorized();
              }

              const hasRequired = entitlements.some((e) =>
                REQUIRED_ENTITLEMENT_IDS.includes(e.id)
              );
              if (!hasRequired) {
                return goToUnauthorized();
              }

              entitlementsService.set(entitlements, userInfo);
              return true;
            })
          );
        })
      );
    })
  );
};

export const redirectWhenYfSsidExistsGuard: CanActivateFn = () => {
  const document = inject(DOCUMENT);
  const router = inject(Router);
  const loginInformationService = inject(LoginInformationService);
  const tenantOnboardingService = inject(TenantOnboardingService);

  if (isFakeLoginEnabled()) {
    document.cookie = 'YF_SSID=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    // A fake login may pick another tenant, whose onboarding status must be loaded again.
    tenantOnboardingService.clear();
    return true;
  }

  return loginInformationService.validate().pipe(
    map((result) => (result === 'valid' ? router.createUrlTree(['/Home']) : true))
  );
};

export const rootHomeRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  const entitlementsService = inject(EntitlementsService);

  if (entitlementsService.has('KBJPROCES')) {
    return router.createUrlTree(['/proces/home']);
  }
  if (entitlementsService.has('KBJCONFIG')) {
    return router.createUrlTree(['/config/home']);
  }
  return router.createUrlTree(['/Home']);
};

export const configHomeRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  const entitlementsService = inject(EntitlementsService);

  return entitlementsService.has('KBJCONFIG')
    ? router.createUrlTree(['/config/home'])
    : router.createUrlTree(['/Home']);
};

export const procesHomeRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  const entitlementsService = inject(EntitlementsService);

  return entitlementsService.has('KBJPROCES')
    ? router.createUrlTree(['/proces/home'])
    : router.createUrlTree(['/Home']);
};
